import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Vault, CheckCircle2, Check } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_TYPES = ["Independent Artist", "Record Label", "Producer / Manager"];
const FUNDING_TYPES = [
  "Artist Capital Loan",
  "Label Line of Credit",
  "Business Credit Card",
  "Catalog Advance",
  "Not Sure — Advise Me",
];
const AMOUNTS = ["$25K – $100K", "$100K – $500K", "$500K – $1M", "$1M – $5M", "$5M+"];
const REVENUES = ["Under $5K / mo", "$5K – $25K / mo", "$25K – $100K / mo", "$100K+ / mo"];
const CATALOG_AGES = ["Just starting", "1 – 3 years", "3 – 7 years", "7+ years"];
const STEP_LABELS = ["Entity", "Funding", "Metrics", "Contact"];

const BARCODE_BG =
  "repeating-linear-gradient(90deg,#4D4232 0 2px,transparent 2px 5px,#4D4232 5px 9px,transparent 9px 11px,#4D4232 11px 12px,transparent 12px 17px)";

const OptionGrid = ({ options, value, onChange, testid }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={testid}>
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        data-testid={`${testid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onChange(opt)}
        className={`text-left border p-5 text-sm transition-all duration-300 ${
          value === opt
            ? "border-[#c9a227] bg-[#fdf6e3] text-[#4D4232] gold-border-glow"
            : "border-[#c9a227]/25 bg-white text-sand hover:border-[#c9a227]/60"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="block text-xs font-mono-accent uppercase tracking-[0.2em] text-stone mb-3">
      {label}
    </label>
    <input
      {...props}
      data-testid={testid}
      className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-5 py-4 text-sm text-cream placeholder:text-stone/60 focus:outline-none focus:border-[#c9a227] transition-colors"
    />
  </div>
);

const Stamp = ({ index }) => (
  <motion.div
    initial={{ scale: 2.6, opacity: 0, rotate: -35 }}
    animate={{ scale: 1, opacity: 1, rotate: -12 }}
    transition={{ type: "spring", stiffness: 280, damping: 14 }}
    data-testid={`pass-stamp-${index + 1}`}
    className="w-16 h-16 rounded-full border-[3px] border-[#c9a227] bg-white/70 flex flex-col items-center justify-center text-[#c9a227] shadow-sm"
  >
    <Check className="w-4 h-4" />
    <span className="text-[8px] font-mono-accent uppercase tracking-[0.12em] mt-0.5">
      Step 0{index + 1}
    </span>
  </motion.div>
);

const Barcode = () => (
  <div className="mt-10 pt-6 border-t border-dashed border-[#c9a227]/60">
    <div className="h-10 opacity-90" style={{ background: BARCODE_BG }} />
    <p className="mt-2 text-[10px] font-mono-accent tracking-[0.4em] text-stone text-center uppercase">
      Emison · Vault Access
    </p>
  </div>
);

const Apply = () => {
  const [step, setStep] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [form, setForm] = useState({
    entity_type: "",
    funding_type: "",
    amount: "",
    monthly_streams: "",
    monthly_revenue: "",
    catalog_age: "",
    name: "",
    artist_label_name: "",
    email: "",
    phone: "",
    music_link: "",
    message: "",
  });

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const canContinue = [
    !!form.entity_type,
    !!form.funding_type && !!form.amount,
    true,
    !!form.name && !!form.email && !!form.artist_label_name,
  ][step];

  const goNext = () => {
    if (!canContinue) return;
    setCompleted((c) => (c.includes(step) ? c : [...c, step]));
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setCompleted((c) => (c.includes(3) ? c : [...c, 3]));
    try {
      const res = await axios.post(`${API}/applications`, form);
      setReference(res.data.reference);
      toast.success("Application received — check your inbox for confirmation.");
    } catch (e) {
      toast.error("Something went wrong submitting your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <main data-testid="apply-success" className="pt-20 min-h-screen flex items-center">
        <div className="max-w-2xl mx-auto px-6 py-32 text-center w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-[#fffdf9] border border-[#e3d3ac] p-12 lg:p-16 shadow-[0_30px_80px_rgba(45,26,11,0.22)]"
          >
            <span
              className="absolute bottom-0 left-0 right-0 h-[3px]"
              style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
            />
            <span
              className="mx-auto w-12 h-12 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
              style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
            >
              <Vault className="-rotate-45 w-5 h-5 text-white" />
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl text-cream">
              The vault is <span className="italic gold-text">open.</span>
            </h1>
            <p className="mt-6 text-base text-sand leading-relaxed">
              Your application has been received. Our team is reviewing your
              profile and will come back with options — typically within 24
              hours. A confirmation is on its way to your inbox.
            </p>
            <p className="mt-8 text-xs font-mono-accent uppercase tracking-[0.3em] text-stone">
              Your seat number
            </p>
            <p
              data-testid="apply-reference-code"
              className="mt-3 font-mono-accent text-2xl tracking-[0.3em] text-gold"
            >
              {reference}
            </p>
            <p className="mt-4 text-xs text-sand leading-relaxed max-w-sm mx-auto">
              Save your seat number — together with your email, it opens{" "}
              <a href="/dashboard" className="text-champagne underline underline-offset-4 decoration-[#c9a227]/50 hover:text-gold transition-colors">
                your vault file
              </a>
              , where you can track your status and upload documents.
            </p>
            <motion.div
              initial={{ scale: 2.4, opacity: 0, rotate: -28 }}
              animate={{ scale: 1, opacity: 1, rotate: -10 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 220, damping: 13 }}
              className="mt-8 mx-auto w-36 h-36 rounded-full border-4 border-[#c9a227] flex items-center justify-center text-[#c9a227] bg-white/60"
            >
              <span className="text-xs font-mono-accent uppercase tracking-[0.2em] leading-relaxed text-center">
                Access
                <br />
                Granted
              </span>
            </motion.div>
            <div className="mt-10">
              <div className="h-10 opacity-90" style={{ background: BARCODE_BG }} />
            </div>
            <a
              data-testid="apply-success-book-call"
              href={`/book?ref=${reference}&name=${encodeURIComponent(form.name)}&email=${encodeURIComponent(form.email)}`}
              className="sheen-btn mt-10 inline-flex items-center gap-3 text-[#4D4232] text-sm font-semibold uppercase tracking-[0.25em] px-10 py-5"
            >
              Schedule Your Free Strategy Call
            </a>
            <div>
              <a
                href="/"
                data-testid="apply-success-home"
                className="btn-outline-lux mt-2 inline-flex items-center gap-3 text-champagne text-[11px] font-semibold uppercase tracking-[0.25em] px-8 py-4 hover:text-gold"
              >
                Return Home
              </a>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="apply-page" className="pt-20 relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(900px 420px at 50% 8%, rgba(212,175,55,0.12), transparent 65%)" }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute top-24 left-1/2 -translate-x-1/2 font-serif italic whitespace-nowrap text-[22vw] leading-none text-transparent"
        style={{ WebkitTextStroke: "1px rgba(212,175,55,0.10)" }}
      >
        Vault
      </span>
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 max-w-3xl mx-auto text-center">
        <span
          className="mx-auto w-12 h-12 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
          style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
        >
          <span className="-rotate-45 font-serif italic text-white text-base">E</span>
        </span>
        <div className="flex items-center justify-center gap-4 mb-8">
          <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#d4af37]" />
          <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
          <p className="text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold">
            The Vault — Application
          </p>
          <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
          <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#d4af37]" />
        </div>
        <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-[#f3e5c8]">
          <MaskedLines
            lines={[
              <>Your backstage pass</>,
              <span className="italic gold-text">to capital.</span>,
            ]}
          />
        </h1>
        <p className="mt-8 text-base text-[#cbb493] leading-relaxed max-w-xl mx-auto">
          Four steps. Each one earns a stamp on your pass. No obligation, no
          hard credit pull — a real assessment within 24 hours.
        </p>
      </section>

      <section className="px-6 lg:px-12 pb-32 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <p className="text-[10px] font-mono-accent uppercase tracking-[0.3em] text-gold">
            Access Level {step + 1} / 4 — {STEP_LABELS[step]}
          </p>
          <div className="hidden sm:flex items-center gap-1.5">
            {STEP_LABELS.map((_, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <span
                  className={`w-3 h-3 rotate-45 transition-all duration-500 ${
                    i < step
                      ? "bg-[#c9a227]"
                      : i === step
                      ? "shadow-[0_0_12px_rgba(212,175,55,0.6)]"
                      : "border border-[#c9a227]/30"
                  }`}
                  style={i === step ? { background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" } : {}}
                />
                {i < STEP_LABELS.length - 1 && <span className={`h-px w-5 ${i < step ? "bg-[#c9a227]" : "bg-[#c9a227]/25"}`} />}
              </span>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative bg-[#fffdf9] border border-[#e3d3ac] flex shadow-[0_40px_100px_rgba(45,26,11,0.3),0_0_60px_rgba(212,175,55,0.12)]"
          >
            <span
              className="absolute top-0 left-0 right-0 h-[3px] z-10"
              style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
            />
            <span
              className="absolute bottom-0 left-0 right-0 h-[3px] z-10"
              style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
            />
            <div className="absolute inset-2.5 border border-[#c9a227]/25 pointer-events-none z-10" />

            <div className="hidden sm:flex w-16 shrink-0 bg-[#4D4232] items-center justify-center border-r-2 border-dashed border-[#c9a227]">
              <span
                style={{ writingMode: "vertical-rl" }}
                className="text-[10px] font-mono-accent uppercase tracking-[0.5em] text-[#d4af37] rotate-180 whitespace-nowrap"
              >
                Backstage Pass · Emison Management
              </span>
            </div>

            <div className="flex-1 p-8 lg:p-12">
              <div className="flex items-center justify-between min-h-[64px] mb-6">
                <span
                  className="w-10 h-10 rotate-45 flex items-center justify-center shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
                  style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
                >
                  <span className="-rotate-45 font-serif italic text-white text-sm">E</span>
                </span>
                <div className="flex gap-3">
                  {completed.map((s) => (
                    <Stamp key={s} index={s} />
                  ))}
                </div>
              </div>

              {step === 0 && (
                <div data-testid="apply-form-step-1-type">
                  <h2 className="font-serif text-2xl text-champagne mb-8">Who are we funding?</h2>
                  <OptionGrid
                    options={ENTITY_TYPES}
                    value={form.entity_type}
                    onChange={(v) => setForm((f) => ({ ...f, entity_type: v }))}
                    testid="apply-entity-option"
                  />
                </div>
              )}

              {step === 1 && (
                <div data-testid="apply-form-step-2-amount" className="space-y-10">
                  <div>
                    <h2 className="font-serif text-2xl text-champagne mb-8">What kind of capital?</h2>
                    <OptionGrid
                      options={FUNDING_TYPES}
                      value={form.funding_type}
                      onChange={(v) => setForm((f) => ({ ...f, funding_type: v }))}
                      testid="apply-funding-option"
                    />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-champagne mb-6">How much do you need?</h3>
                    <OptionGrid
                      options={AMOUNTS}
                      value={form.amount}
                      onChange={(v) => setForm((f) => ({ ...f, amount: v }))}
                      testid="apply-amount-option"
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="apply-form-step-3-metrics" className="space-y-8">
                  <h2 className="font-serif text-2xl text-champagne">Your numbers</h2>
                  <Field
                    label="Approximate monthly streams (all platforms)"
                    placeholder="e.g. 850,000"
                    value={form.monthly_streams}
                    onChange={set("monthly_streams")}
                    testid="apply-input-streams"
                  />
                  <div>
                    <label className="block text-xs font-mono-accent uppercase tracking-[0.2em] text-stone mb-3">
                      Monthly royalty / music revenue
                    </label>
                    <OptionGrid
                      options={REVENUES}
                      value={form.monthly_revenue}
                      onChange={(v) => setForm((f) => ({ ...f, monthly_revenue: v }))}
                      testid="apply-revenue-option"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono-accent uppercase tracking-[0.2em] text-stone mb-3">
                      Catalog age
                    </label>
                    <OptionGrid
                      options={CATALOG_AGES}
                      value={form.catalog_age}
                      onChange={(v) => setForm((f) => ({ ...f, catalog_age: v }))}
                      testid="apply-catalog-option"
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="apply-form-step-4-contact" className="space-y-8">
                  <h2 className="font-serif text-2xl text-champagne">Where do we send your offer?</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <Field label="Full name *" placeholder="Your name" value={form.name} onChange={set("name")} testid="apply-input-name" />
                    <Field label="Artist / label name *" placeholder="Stage or label name" value={form.artist_label_name} onChange={set("artist_label_name")} testid="apply-input-artist-name" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <Field label="Email *" type="email" placeholder="you@example.com" value={form.email} onChange={set("email")} testid="apply-input-email" />
                    <Field label="Phone" placeholder="(optional)" value={form.phone} onChange={set("phone")} testid="apply-input-phone" />
                  </div>
                  <Field label="Spotify / Apple Music / website link" placeholder="https://…" value={form.music_link} onChange={set("music_link")} testid="apply-input-music-link" />
                  <div>
                    <label className="block text-xs font-mono-accent uppercase tracking-[0.2em] text-stone mb-3">
                      Anything else we should know?
                    </label>
                    <textarea
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      data-testid="apply-input-message"
                      placeholder="Upcoming release, tour, signing plans…"
                      className="w-full bg-[#faf7ef] border border-[#c9a227]/30 px-5 py-4 text-sm text-cream placeholder:text-stone/60 focus:outline-none focus:border-[#c9a227] transition-colors resize-none"
                    />
                  </div>
                </div>
              )}

              <div className="mt-12 flex items-center justify-between">
                <button
                  type="button"
                  data-testid="apply-back-button"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 text-xs font-mono-accent uppercase tracking-[0.2em] text-stone hover:text-champagne transition-colors disabled:opacity-0"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                {step < 3 ? (
                  <button
                    type="button"
                    data-testid="apply-next-button"
                    onClick={goNext}
                    disabled={!canContinue}
                    className="sheen-btn inline-flex items-center gap-3 text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-8 py-4 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.03] transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" }}
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="apply-form-submit-button"
                    onClick={submit}
                    disabled={!canContinue || submitting}
                    className="sheen-btn inline-flex items-center gap-3 text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-8 py-4 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.03] transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" }}
                  >
                    {submitting ? "Heading Backstage…" : "Go Backstage"}
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <Barcode />
            </div>
          </motion.div>
        </AnimatePresence>
      </section>
    </main>
  );
};

export default Apply;
